'use client'

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { useEffect, useMemo, useState } from 'react'

import { ProductGalleryDS } from '@src/components/blocks/product-gallery-ds'
import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { useSelectionMap, useWidgetOfType } from '@src/components/pl-base-widget/contexts/page-widget-store'
import { usePdpSelectionDerived } from '@src/components/pl-base-widget/contexts/pdp-selection'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { SliderImageWithText } from '@src/quince-components/src'
import Directory from '@src/quince-site-common/src/domains/Directory'
import type SvdProduct from '@src/quince-site-common/src/domains/svd/svd-product'
import { getSvdGalleryVariant } from '@src/quince-site-common/src/helpers/svd-gallery-helper'
import {
  getSvdPdpWishlistAnchorFields,
  getSvdPdpWishlistEntry,
} from '@src/quince-site-common/src/helpers/svd-wishlist-helper'
import { useSvdWishlist } from '@src/quince-site-common/src/hooks/use-svd-wishlist'
import { serializeSelectedAttributes } from '@src/quince-site-common/src/services/Wishlist.types'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { protoToSvdBundle } from '../bundle-purchase-v2/bundle-purchase-v2.adapter'

import { buildBundleGalleryMedia } from './bundle-gallery-v2.helpers'
import styles from './bundle-gallery-v2.module.scss'

import type { BundleGalleryV2WidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

/** Breadcrumbs are their own PL widget on the PDP, so the gallery renders without its own. */
const EMPTY_DIRECTORY = new Directory({ paths: [] })

// eslint-disable-next-line i18next/no-literal-string -- telemetry tracker-section identifier, not copy
const WISHLIST_TRACKER_SECTION = 'pdp_page'

/**
 * BUNDLE_GALLERY_V2 widget — the SVD bundle media gallery, emitted instead of PRODUCT_GALLERY on an
 * `enrichVersion=V2` bundle session.
 *
 * Self-contained by design. Bundle assets are scoped ONLY by their inline `vdas` map — the proto carries no
 * per-variant media refs (unlike a normal-product V2 gallery), so there is nothing to join against a variant
 * id. The widget therefore reads just the selection published on the page-widget store, which for a bundle
 * is the FIRST REQUIRED CHILD's anchor selection (decision D5 — V1 parity, where `BundleProductImages`
 * follows `products[0]`), and matches it against each asset. See `bundle-gallery-v2.helpers` for the
 * matching + fallback rules.
 *
 * Renders through the same two implementations as every other PDP gallery, so the DS ramp is honoured:
 * `ProductGalleryDS` on the DS path, `SliderImageWithText` on the legacy path.
 *
 * **Wishlist.** A bundle keeps the non-PL rule that the heart is hidden (`hideWishList`, matching
 * `bundle-product-images-legacy.tsx:58` and the payload's own `flags.showWishlistOnImage: false`) — but
 * "hidden" is not absolute: both gallery implementations let the PDP_BREADCRUMB experiment override it
 * (`showWishListIconOnImage = showWishlistOnImage || !hideWishList`), and that override is computed inside
 * the shared atoms where a widget cannot suppress it. Non-PL bundles pass no toggle at all, so in those
 * experiment groups they render a heart that does nothing. We wire a real toggle instead: whenever the
 * heart renders it works, keyed SVD-natively off the FIRST REQUIRED CHILD — the same constituent the
 * gallery's media already follows (D5) — via its primary swatch VDA rather than a legacy colour option.
 */
function BundleGalleryV2(props: PLTypedWidgetProps<BundleGalleryV2WidgetData>) {
  const { bundleGalleryV2Data } = props.data as Partial<typeof props.data>
  const selectionMap = useSelectionMap()

  const media = useMemo(
    () => buildBundleGalleryMedia(bundleGalleryV2Data?.media, selectionMap, bundleGalleryV2Data?.flags),
    [bundleGalleryV2Data, selectionMap],
  )

  // The wishlist subject: the first required constituent, read from the sibling buy-box payload (the same
  // cross-widget pattern the V1 gallery uses to reach PRODUCT_PURCHASE). The gallery payload itself carries
  // no product identity — only assets — so there is nothing local to key a wishlist entry on.
  const bundlePurchaseData = useWidgetOfType(WidgetType.BUNDLE_PURCHASE_V2)?.widget.data?.bundlePurchaseV2Data
  const bundleAdapted = useMemo(
    () => (bundlePurchaseData ? protoToSvdBundle(bundlePurchaseData) : undefined),
    [bundlePurchaseData],
  )
  const firstChild = bundleAdapted?.bundle.customBundleProducts?.[0] as unknown as SvdProduct | undefined

  const publishedFirstChildVariantId = usePdpSelectionDerived()?.bundleSelections?.[0]?.variantId
  const resolvedVariantId = publishedFirstChildVariantId ?? bundleAdapted?.requiredSelections[0]?.variantId
  const svdVariant = useMemo(() => getSvdGalleryVariant(firstChild, resolvedVariantId), [firstChild, resolvedVariantId])
  const wishlistEntry = useMemo(
    () => (firstChild ? getSvdPdpWishlistEntry(firstChild, svdVariant) : undefined),
    [firstChild, svdVariant],
  )
  const wishlistScopeKey = serializeSelectedAttributes(wishlistEntry?.selectedAttributes)

  const [isWishListed, setIsWishListed] = useState(false)
  const { isItemInWishList, addItemToWishList, removeItemFromWishList } = useSvdWishlist()
  useEffect(() => {
    if (!wishlistEntry) return
    setIsWishListed(isItemInWishList(wishlistEntry.productId, wishlistEntry.selectedAttributes))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- wishlistScopeKey is the stable form of selectedAttributes
  }, [isItemInWishList, wishlistEntry?.productId, wishlistScopeKey])

  const onWishListToggle = async () => {
    if (!wishlistEntry || !firstChild) return
    setIsWishListed(!isWishListed)
    // The anchor keys ride in `context` — the one place `trackSvdCardEvent` forwards to the wire — and
    // the seam injects them from the fields passed here. No card variant on the PDP, so this supplies
    // its own from the anchor VDA.
    const trackConfig = { trackerSection: WISHLIST_TRACKER_SECTION }
    const anchorFields = getSvdPdpWishlistAnchorFields(firstChild, svdVariant)
    if (isWishListed) {
      await removeItemFromWishList(wishlistEntry.productId, wishlistEntry.selectedAttributes, trackConfig, anchorFields)
    } else {
      await addItemToWishList(wishlistEntry, trackConfig, anchorFields)
    }
  }

  const wishListToggleProp = firstChild?.id
    ? () => {
        void onWishListToggle()
      }
    : undefined

  const pdpHeroImageId = TEST_LOCATORS.SLIDER_IMAGES_MAIN_IMAGE

  // Do NOT bail out on empty media: non-PL renders its gallery unconditionally so the column still reserves
  // the image space, and returning null here would collapse it (the V1 widget carries the same note).
  if (shouldUseDesignSystem()) {
    return (
      <ProductGalleryDS
        media={media}
        directory={EMPTY_DIRECTORY}
        // Bundle panel sizing + the bundle tag branch inside the DS gallery (non-PL passes the same flag).
        isBundle
        hideBreadCrumb
        // Bundle rule stays `hideWishList` (non-PL parity); the toggle only matters where the
        // PDP_BREADCRUMB experiment overrides it inside the gallery atoms.
        hideWishList
        isWishListed={isWishListed}
        onWishListToggle={wishListToggleProp}
        hasSelectedOptionImages
        pdpHeroImageId={pdpHeroImageId}
      />
    )
  }

  return (
    <SliderImageWithText
      className={styles.gallery}
      sliderImageProps={{
        media,
        // Media is already scoped to the current selection by `buildBundleGalleryMedia`.
        hasSelectedOptionImages: true,
        shouldLoop: false,
        pdpHeroImageId,
        isWishListed,
        onWishListToggle: wishListToggleProp,
      }}
      directory={EMPTY_DIRECTORY}
    />
  )
}

export const BundleGalleryV2Widget: PLTypedWidgetInterface<BundleGalleryV2WidgetData, WidgetType.BUNDLE_GALLERY_V2> = {
  id: WidgetType.BUNDLE_GALLERY_V2,
  errorBoundary: PLBaseErrorBoundary,
  render: BundleGalleryV2,
}
